import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import moment from 'moment';

import {IMyOptions as IDatePickerMyOptions, IMyDate, IMyDateModel, IMyDayLabels} from '@intocare/ui-my-date-picker';
import {IMyOptions as IDateRangePickerMyOptions, IMyDateRangeModel} from '@intocare/ui-my-date-range-picker';

const DATE_FORMAT = new Map([
	['month', 'YYYY-MM'],
	['day', 'YYYY-MM-DD'],
	['week', 'GGGG-[W]W'],
	['year', 'YYYY']
]);

@Component({
	selector: 'ic-date-filter',
	templateUrl: './date-filter.component.html',
	styleUrls: ['./date-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DateFilterComponent implements OnInit, OnChanges {
	private translator = inject(TranslateService);
	private elRef = inject(ElementRef);

	@Output() date = new EventEmitter();
	@Output() cleared = new EventEmitter();
	@Input() types: string[];
	@Input() disableDatesBefore: moment.Moment;
	@Input() disableDatesAfter: moment.Moment;
	@Input() disabled = false;
	@Input() initialSelectedDate: string;

	selected: string;
	dropDown: string;
	currentDate: moment.Moment;
	currentRange: string;
	dayLabels: IMyDayLabels;
	locale: string;
	selDate: IMyDate;
	datePickerOptions: IDatePickerMyOptions & IDateRangePickerMyOptions;
	disableBefore: IMyDate;
	disableAfter: IMyDate;
	customRange = true;

	@HostListener('document:click', ['$event'])
	clickout(event) {
		if (!this.elRef.nativeElement.contains(event.target)) {
			this.dropDown = '';
			this.customRange = false;
		}
	}

	get weekNumber() {
		return {
			nr: this.currentDate.format('w')
		};
	}

	ngOnInit() {
		this.locale = this.translator.currentLang;
		moment.locale(this.locale);

		// Set initial selected date
		this.setInitialSelectedDate();

		if (!this.types) {
			this.types = ['day', 'week', 'month', 'year'];
		}
		this.selected = this.types[0];
		this.translator.get('time').subscribe(time => {
			this.dayLabels = {
				su: time.su,
				mo: time.mo,
				tu: time.tu,
				we: time.we,
				th: time.th,
				fr: time.fr,
				sa: time.sa
			};
		});
		this.disableDatesBefore = this.disableDatesBefore || moment('01-01-1950', 'MM-DD-YYYY');
		this.disableDatesAfter = this.disableDatesAfter || moment().add(1, 'd');
		this.disableBefore = {
			day: Number(this.disableDatesBefore.format('D')),
			month: Number(this.disableDatesBefore.format('M')),
			year: Number(this.disableDatesBefore.format('YYYY'))
		};
		this.disableAfter = {
			day: Number(this.disableDatesAfter.format('D')),
			month: Number(this.disableDatesAfter.format('M')),
			year: Number(this.disableDatesAfter.format('YYYY'))
		};
		this.setDatePickerOptions(this.selected);
	}

	isActive(type: string) {
		if (type === this.selected) {
			return 'primary';
		}

		return 'secondary';
	}

	setDateType(type: string, week?: number) {
		this.dropDown = '';
		this.selected = type;

		let weekNumber = week;

		if (type !== 'range') {
			weekNumber = week || this.currentDate.isoWeek();
		}

		let date;

		switch (type) {
			case 'day':
				date = this.currentDate.format('YYYY-MM-DD');
				break;
			case 'week':
				date = `${this.currentDate.format('GGGG')}-W${weekNumber}`;
				break;
			case 'month':
				date = this.currentDate.format('YYYY-MM');
				break;
			case 'year':
				date = this.currentDate.format('YYYY');
				break;
			default:
				date = this.currentDate;
		}

		this.date.emit(date);
	}

	protected getLabel(type: string) {
		if (type !== 'range') {
			if (this.selected !== type) {
				return `time.${type}`;
			}

			switch (type) {
				case 'day':
					return this.currentDate.format('DD/MM/YY');
				case 'week':
					return 'time.week_x';
				case 'month':
					return this.currentDate.format('MMM');
				case 'year':
					return this.currentDate.format('YYYY');
				default:
					return this.currentDate.toString();
			}
		}

		if (this.currentRange === '') {
			return `time.${type}`;
		}

		return this.currentRange;
	}

	toggleDropDown(type: string) {
		if (this.dropDown === type) {
			this.dropDown = '';
		} else {
			this.setDatePickerOptions(type);
			this.dropDown = type;
		}
		this.customRange = true;
	}

	private setDatePickerOptions(type: string) {
		this.translator.get(['time']).subscribe(translations => {
			let todayBtn = translations.today;
			if (type === 'week') {
				todayBtn = translations.week;
			}

			this.datePickerOptions = {
				inline: true,
				dateFormat: 'yyyy-mm-dd',
				selectorHeight: '200px',
				selectorWidth: '180px',
				markCurrentDay: type === 'day',
				markCurrentWeek: type === 'week',
				markCurrentMonth: type === 'month',
				markCurrentYear: type === 'year',
				todayBtnTxt: todayBtn,
				showWeekNumbers: type === 'week',
				selectWeekOnly: type === 'week',
				selectMonthOnly: type === 'month',
				selectYearOnly: type === 'year',
				hideMonthArrows: true,
				hideYearArrows: true,
				dayLabels: this.dayLabels,
				disableUntil: this.disableBefore,
				disableSince: this.disableAfter,
				selectYearText: translations.selectYear
			};

			if (type !== 'range') {
				return;
			}

			this.datePickerOptions = {
				...this.datePickerOptions,
				showClearBtn: false,
				showApplyBtn: false,
				selectBeginDateTxt: translations.first,
				selectEndDateTxt: translations.last
			};
		});
	}

	showDropDown(type: string) {
		return type === this.dropDown;
	}

	selectDate(event: IMyDateModel, type: string) {
		this.currentDate = moment(event.formatted, 'YYYY-MM-DD');
		this.selDate = {
			day: this.currentDate.get('date'),
			month: (this.currentDate.get('month') as number) + 1,
			year: this.currentDate.get('year')
		};
		this.setDateType(type, event.date.week);
	}

	selectRange(days: number, event?: IMyDateRangeModel) {
		if (event) {
			const split = event.formatted.split(' - ');
			const startDate = moment(split[0], 'YYYY-MM-DD');
			const endDate = moment(split[1], 'YYYY-MM-DD');
			this.currentDate = {
				from: startDate.format('YYYY-MM-DD'),
				to: endDate.format('YYYY-MM-DD')
			} as any;
			this.currentRange = `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
			this.dropDown = '';
			this.customRange = !this.customRange;
			this.setDateType('range');
		} else if (days > 0) {
			this.currentRange = `time.last${days}Days`;
			const startDate = moment();
			const endDate = moment().subtract(days, 'days');
			this.currentDate = {
				to: startDate.format('YYYY-MM-DD'),
				from: endDate.format('YYYY-MM-DD')
			} as any;
			this.dropDown = '';
			this.setDateType('range');
		} else {
			this.currentRange = '';
			this.customRange = !this.customRange;
		}
	}

	showBlock(type: string) {
		return this.types.indexOf(type) + 1;
	}

	clearRange() {
		this.currentDate = moment();
		this.currentRange = '';
		this.cleared.emit(true);
	}

	/**
	 * Sets the initial selected date
	 */
	private setInitialSelectedDate() {
		// In case a date was passed through the input use that one, if not, use today
		const initialSelectedDateMoment = moment(this.initialSelectedDate, DATE_FORMAT.get(this.selected), true);
		this.currentDate = initialSelectedDateMoment.isValid() ? initialSelectedDateMoment : moment();

		this.currentRange = '';

		this.selDate = {
			day: this.currentDate.get('date'),
			month: (this.currentDate.get('month') as number) + 1,
			year: this.currentDate.get('year')
		};
	}

	ngOnChanges(changes: SimpleChanges) {
		// In case there's a change in the initialSelectedDate, we update it
		if (changes.initialSelectedDate && this.initialSelectedDate) {
			this.setInitialSelectedDate();
		}
	}
}
